import React from 'react'

import { Timestamp, timestampDate } from '@bufbuild/protobuf/wkt'
import { useSuspenseQuery } from '@connectrpc/connect-query'
import { formatDistanceToNow } from 'date-fns'
import { ja } from 'date-fns/locale/ja'
import { useTranslation } from 'react-i18next'

import { Text } from '@chakra-ui/react'

import {
  FlexTable,
  FlexTableBody,
  FlexTableCell,
  FlexTableHeader,
  FlexTableRow,
  TertiaryButton,
} from '@enechain/ecloud-designsystem'

import { listSessions } from '~/gen/proto/bff/v1/common-BffCommonService_connectquery'
import { SessionInformation } from '~/gen/proto/bff/v1/model_pb'
import i18n from '~/i18n'
import { fromTimestamp } from '~/utils/date'

type Prop = {
  onFinish: (sessionInformation: SessionInformation) => void
}

export const SessionListTable: React.FC<Prop> = ({ onFinish }) => {
  const { t } = useTranslation()
  const { data: sessions } = useSuspenseQuery(listSessions)
  const dateLocale = i18n.language === 'en' ? undefined : ja
  const now = new Date()
  const isWithin24Hours = (date: Timestamp): boolean =>
    now.getTime() - timestampDate(date).getTime() < 24 * 60 * 60 * 1000

  return (
    <FlexTable
      borderRadius="radiiBase"
      borderWidth={1}
      columnStyles={{
        button: {
          alignment: 'right',
        },
      }}
    >
      <FlexTableHeader style="single-line">
        <FlexTableRow>
          <FlexTableCell
            contents={{ text: t('pages.profile.label.device') }}
            columnKey="device"
          />
          <FlexTableCell
            contents={{ text: t('pages.profile.label.browser') }}
            columnKey="browser"
          />
          <FlexTableCell
            contents={{ text: t('pages.profile.label.loginAt') }}
            columnKey="loginAt"
          />
          <FlexTableCell columnKey="currentSession" />
          <FlexTableCell columnKey="button" />
        </FlexTableRow>
      </FlexTableHeader>
      <FlexTableBody style="multi-line">
        {sessions.sessions.map((item) => (
          <FlexTableRow key={item.sessionKey}>
            <FlexTableCell columnKey="device">{item.platform}</FlexTableCell>
            <FlexTableCell columnKey="browser">{item.browser}</FlexTableCell>
            <FlexTableCell columnKey="loginAt">
              {item.loginAt
                ? isWithin24Hours(item.loginAt)
                  ? formatDistanceToNow(timestampDate(item.loginAt), {
                      addSuffix: true,
                      locale: dateLocale,
                    })
                  : fromTimestamp(item.loginAt).formatYYYYMMDD()
                : ''}
            </FlexTableCell>
            <FlexTableCell columnKey="currentSession">
              {item.current && (
                <Text
                  width="fit-content"
                  borderRadius="radiiLg"
                  backgroundColor="Surface.positiveMid"
                  paddingX="2"
                  paddingY="1"
                >
                  {t('pages.profile.label.currentSession')}
                </Text>
              )}
            </FlexTableCell>
            <FlexTableCell columnKey="button">
              <TertiaryButton
                disabled={item.current}
                onClick={() => onFinish(item)}
                variant="outline"
              >
                {t('pages.profile.label.logout')}
              </TertiaryButton>
            </FlexTableCell>
          </FlexTableRow>
        ))}
      </FlexTableBody>
    </FlexTable>
  )
}
